/**
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * predictedFragments.ts — 예상 파편(Predicted Fragment) 자동 생성
 *
 * 【동작 원리】
 *   학습 기록 + 오개념 기록 + 단계 미완료 여부를 분석하여
 *   "이 학생이 다음에 틀릴 가능성이 높은 개념 파편"을 자동 추천합니다.
 *
 * 【파편 우선순위 계산】
 *   priority = (오개념 hitCount × 30) + (미완료 단계 × 20) + (전제 단계 미완료 × 15) + 기본점수
 *
 * 【활용 방법】
 *   - N1LearnPage: 사이드패널에 "오늘 꼭 봐야 할 파편" 표시
 *   - N1TestPage: 오답 분석 후 연관 파편 추천
 *   - Dashboard: 학습 추천 위젯
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

import { UnitProgress } from './progressStore'

// ─────────────────────────────────────────────────────────────
// 소인수분해(N1-1) 파편 정의
// ─────────────────────────────────────────────────────────────

/** 파편 하나의 설명 */
export interface FragmentDef {
  id: string              // 'FRAG-NS01-NM01'
  stepId: string          // 'NS01'
  stepTitle: string       // '소수와 합성수'
  misconceptionCode: string   // 'NM01'
  misconceptionLabel: string  // '1을 소수로 착각'
  title: string           // 파편 제목 (짧게)
  description: string     // 왜 틀리는지 설명
  fixTip: string          // 고치는 방법 1줄
  emoji: string
  prerequisiteStepIds: string[]  // 이 파편을 이해하려면 먼저 완료해야 할 단계
}

/** 예상 파편 추천 결과 */
export interface PredictedFragment {
  fragment: FragmentDef
  priority: number        // 높을수록 급하게 봐야 함
  reason: string[]        // 추천 이유 목록 (UI 표시용)
  tag: 'danger' | 'warning' | 'review'  // 위험 / 주의 / 복습
}

// ─────────────────────────────────────────────────────────────
// N1-1 소인수분해 파편 데이터베이스
// ─────────────────────────────────────────────────────────────
export const N1_FRAGMENT_DB: FragmentDef[] = [
  // ── NS01: 소수와 합성수 ──────────────────────────────────
  {
    id: 'FRAG-NS01-NM01',
    stepId: 'NS01',
    stepTitle: '소수와 합성수',
    misconceptionCode: 'NM01',
    misconceptionLabel: '1을 소수로 착각',
    title: '1은 소수가 아니다!',
    description: '1은 약수가 {1} 하나뿐이라 소수 정의(약수 2개: 1과 자기 자신)를 만족하지 않습니다.',
    fixTip: '소수 체크리스트: "약수가 딱 2개인가?" → 1은 약수가 1개뿐이므로 소수 아님',
    emoji: '1️⃣',
    prerequisiteStepIds: [],
  },
  {
    id: 'FRAG-NS01-NM02',
    stepId: 'NS01',
    stepTitle: '소수와 합성수',
    misconceptionCode: 'NM02',
    misconceptionLabel: '2가 합성수라고 착각',
    title: '2는 유일한 짝수 소수!',
    description: '"짝수=합성수"라는 오해. 2는 약수가 1과 2뿐이므로 소수입니다.',
    fixTip: '2 이외의 짝수는 모두 합성수. 2는 짝수이지만 소수!',
    emoji: '2️⃣',
    prerequisiteStepIds: [],
  },
  // ── NS02: 거듭제곱과 지수 ────────────────────────────────
  {
    id: 'FRAG-NS02-NM03',
    stepId: 'NS02',
    stepTitle: '거듭제곱과 지수',
    misconceptionCode: 'NM03',
    misconceptionLabel: '2³ = 2×3 = 6으로 계산',
    title: '지수는 곱셈 횟수, 곱할 수가 아니다!',
    description: '2³은 2를 3번 곱한 것(2×2×2=8)이지, 2×3=6이 아닙니다.',
    fixTip: '2³ → "2를 3번 곱해" → 2×2×2 = 8. 지수는 횟수!',
    emoji: '⬆️',
    prerequisiteStepIds: [],
  },
  {
    id: 'FRAG-NS02-NM04',
    stepId: 'NS02',
    stepTitle: '거듭제곱과 지수',
    misconceptionCode: 'NM04',
    misconceptionLabel: '(-2)²과 -2²을 같다고 혼동',
    title: '괄호의 위치가 부호를 바꾼다!',
    description: '(-2)²=(-2)×(-2)=4 이지만, -2²=-(2²)=-(4)=-4 입니다.',
    fixTip: '괄호 있으면 음수 전체를 제곱 → 양수. 괄호 없으면 제곱 후 음수 부호 붙이기',
    emoji: '±',
    prerequisiteStepIds: ['NS02'],
  },
  // ── NS03: 소인수분해 방법 ────────────────────────────────
  {
    id: 'FRAG-NS03-NM05',
    stepId: 'NS03',
    stepTitle: '소인수분해 방법',
    misconceptionCode: 'NM05',
    misconceptionLabel: '소인수분해를 합성수 곱으로 끝냄',
    title: '소인수분해는 소수만의 곱으로!',
    description: '36 = 4×9 처럼 합성수 곱으로 끝내면 틀립니다. 반드시 소수만의 곱으로 나타내야 합니다.',
    fixTip: '결과에 4, 6, 9 같은 합성수가 있으면 소인수분해 미완성! 더 나누세요.',
    emoji: '🌳',
    prerequisiteStepIds: ['NS01', 'NS02'],
  },
  {
    id: 'FRAG-NS03-NM06',
    stepId: 'NS03',
    stepTitle: '소인수분해 방법',
    misconceptionCode: 'NM06',
    misconceptionLabel: '방법에 따라 결과가 달라진다고 착각',
    title: '소인수분해의 결과는 항상 유일하다!',
    description: '인수분해 나무법, 나눗셈 사다리법 중 어떤 방법을 써도 결과는 반드시 같습니다 (산술의 기본정리).',
    fixTip: '결과가 달라 보이면 계산 실수가 있는 것. 방법의 차이가 아님!',
    emoji: '✅',
    prerequisiteStepIds: ['NS01', 'NS02'],
  },
  // ── NS04: 최대공약수(GCD) ────────────────────────────────
  {
    id: 'FRAG-NS04-NM07',
    stepId: 'NS04',
    stepTitle: '최대공약수(GCD)',
    misconceptionCode: 'NM07',
    misconceptionLabel: 'GCD에서 지수가 큰 것을 선택',
    title: 'GCD = 공통 소인수 × 지수 작은 것!',
    description: 'GCD는 두 수의 "공통 부분"이므로 지수가 작은 것을 선택합니다. 큰 것을 선택하면 LCM이 됩니다.',
    fixTip: 'GCD → 지수 작은 것 선택 (교집합). LCM → 지수 큰 것 선택 (합집합)',
    emoji: '🤝',
    prerequisiteStepIds: ['NS01', 'NS02', 'NS03'],
  },
  // ── NS05: 최소공배수(LCM) ────────────────────────────────
  {
    id: 'FRAG-NS05-NM08',
    stepId: 'NS05',
    stepTitle: '최소공배수(LCM)',
    misconceptionCode: 'NM08',
    misconceptionLabel: 'LCM에서 지수가 작은 것을 선택',
    title: 'LCM = 모든 소인수 × 지수 큰 것!',
    description: 'LCM은 두 수를 모두 포함해야 하므로 지수가 큰 것을 선택합니다.',
    fixTip: 'LCM → 지수 큰 것 선택. GCD → 지수 작은 것 선택. 반대로 하면 바뀜!',
    emoji: '✖️',
    prerequisiteStepIds: ['NS01', 'NS02', 'NS03', 'NS04'],
  },
  {
    id: 'FRAG-NS05-NM09',
    stepId: 'NS05',
    stepTitle: '최소공배수(LCM)',
    misconceptionCode: 'NM09',
    misconceptionLabel: 'LCM = 두 수의 곱이라고 착각',
    title: 'LCM ≤ 두 수의 곱 (서로소일 때만 같다)',
    description: '공약수가 있으면 LCM < 두 수의 곱. 예: LCM(4,6)=12 < 4×6=24',
    fixTip: '서로소(공약수가 1뿐)인 경우에만 LCM = 두 수의 곱!',
    emoji: '⚠️',
    prerequisiteStepIds: ['NS04', 'NS05'],
  },
  // ── NS06: GCD·LCM 활용 ───────────────────────────────────
  {
    id: 'FRAG-NS06-NM10',
    stepId: 'NS06',
    stepTitle: 'GCD·LCM 활용 문제',
    misconceptionCode: 'NM10',
    misconceptionLabel: 'GCD/LCM 언제 쓰는지 혼동',
    title: '나눠주기→GCD, 만나기→LCM',
    description: '"최대한 많이 나눠주기"는 GCD. "다시 함께 만나는 시간"은 LCM. 상황을 보고 판단하세요.',
    fixTip: '키워드: 최대로 나누다→GCD / 처음으로 동시에→LCM',
    emoji: '🎯',
    prerequisiteStepIds: ['NS04', 'NS05'],
  },
]

// ─────────────────────────────────────────────────────────────
// N1-2 정수·유리수 파편 데이터베이스
// ─────────────────────────────────────────────────────────────
export const N2_FRAGMENT_DB: FragmentDef[] = [
  // ── NT01: 양수·음수·정수의 개념 ─────────────────────────
  {
    id: 'FRAG-NT01-NM01',
    stepId: 'NT01',
    stepTitle: '양수·음수·정수의 개념',
    misconceptionCode: 'NM01',
    misconceptionLabel: '0을 양수로 착각',
    title: '0은 양수도 음수도 아니다!',
    description: '0은 양수·음수 어느 쪽도 아닙니다. 수직선의 기준점(원점)이에요.',
    fixTip: '양수: +, 음수: -, 0: 기준점. 0에는 ± 부호가 없습니다.',
    emoji: '0️⃣',
    prerequisiteStepIds: [],
  },
  {
    id: 'FRAG-NT01-NM02',
    stepId: 'NT01',
    stepTitle: '양수·음수·정수의 개념',
    misconceptionCode: 'NM02',
    misconceptionLabel: '음수가 실제로 존재하지 않는다고 생각',
    title: '음수는 실생활에서 방향과 크기를 나타낸다!',
    description: '영하 온도(-), 지하층(-), 빚(-)처럼 음수는 반대 방향이나 기준 이하를 표현합니다.',
    fixTip: '음수 = "기준보다 반대 방향". 온도계, 고도, 은행 잔고에서 항상 쓰임!',
    emoji: '↔️',
    prerequisiteStepIds: [],
  },
  // ── NT02: 수직선과 절댓값 ────────────────────────────────
  {
    id: 'FRAG-NT02-NM03',
    stepId: 'NT02',
    stepTitle: '수직선과 절댓값',
    misconceptionCode: 'NM03',
    misconceptionLabel: '절댓값이 음수라고 계산',
    title: '절댓값은 항상 0 이상!',
    description: '|-7|=-7 처럼 계산하는 경우가 많습니다. 절댓값은 "거리"이므로 항상 0 이상입니다.',
    fixTip: '|-7| = 7 (0에서 7만큼 떨어져 있으므로). 절댓값 결과에 음수 없음!',
    emoji: '📏',
    prerequisiteStepIds: ['NT01'],
  },
  {
    id: 'FRAG-NT02-NM04',
    stepId: 'NT02',
    stepTitle: '수직선과 절댓값',
    misconceptionCode: 'NM04',
    misconceptionLabel: '절댓값 기호를 괄호처럼 사용',
    title: '|a| ≠ (a) — 절댓값은 부호를 없애는 것!',
    description: '|3-7| = |3|-|7| 처럼 분배법칙을 적용하면 틀립니다. |3-7|=|-4|=4가 맞습니다.',
    fixTip: '절댓값은 안을 먼저 계산한 뒤 부호 제거. 분배·인수분해 불가!',
    emoji: '🚫',
    prerequisiteStepIds: ['NT01'],
  },
  // ── NT03: 정수의 대소 비교 ───────────────────────────────
  {
    id: 'FRAG-NT03-NM05',
    stepId: 'NT03',
    stepTitle: '정수의 대소 비교',
    misconceptionCode: 'NM05',
    misconceptionLabel: '음수끼리 절댓값이 클수록 크다고 착각',
    title: '음수는 절댓값이 작을수록 더 크다!',
    description: '-9와 -3을 비교할 때 9>3이므로 -9>-3이라 착각합니다. 수직선에서 -3이 오른쪽!',
    fixTip: '수직선: 오른쪽이 항상 큰 수. -3이 -9보다 오른쪽 → -3 > -9',
    emoji: '↔️',
    prerequisiteStepIds: ['NT01', 'NT02'],
  },
  // ── NT04: 정수·유리수의 덧셈·뺄셈 ───────────────────────
  {
    id: 'FRAG-NT04-NM06',
    stepId: 'NT04',
    stepTitle: '정수·유리수의 덧셈·뺄셈',
    misconceptionCode: 'NM06',
    misconceptionLabel: '음수+음수를 양수로 계산',
    title: '같은 부호끼리 더하면 절댓값이 커진다!',
    description: '(-3)+(-5)=+8 처럼 계산하는 실수. 같은 부호끼리는 절댓값을 더하고 공통 부호 유지.',
    fixTip: '(-3)+(-5) → 같은 부호(음수) → -(3+5) = -8',
    emoji: '➕',
    prerequisiteStepIds: ['NT01', 'NT02'],
  },
  {
    id: 'FRAG-NT04-NM07',
    stepId: 'NT04',
    stepTitle: '정수·유리수의 덧셈·뺄셈',
    misconceptionCode: 'NM07',
    misconceptionLabel: '뺄셈에서 부호를 바꾸지 않음',
    title: '뺄셈 → 덧셈 변환: a-b = a+(-b)',
    description: '(-4)-(-9) = -4-9 = -13 처럼 부호를 그대로 두는 실수. 반드시 부호를 바꾸어 덧셈으로!',
    fixTip: '(-4)-(-9) = (-4)+(+9) = +5. "-(-b)"는 항상 "+b"로 변환!',
    emoji: '➖',
    prerequisiteStepIds: ['NT01', 'NT02'],
  },
  // ── NT05: 정수·유리수의 곱셈·나눗셈 ─────────────────────
  {
    id: 'FRAG-NT05-NM08',
    stepId: 'NT05',
    stepTitle: '정수·유리수의 곱셈·나눗셈',
    misconceptionCode: 'NM08',
    misconceptionLabel: '음수×양수 결과 부호 혼동',
    title: '다른 부호 곱셈 → 항상 음수!',
    description: '(-5)×(+4)=+20 처럼 계산하는 실수. 부호가 다른 두 수의 곱·몫은 항상 음수.',
    fixTip: '같은 부호→양수(+), 다른 부호→음수(-). 부호 법칙을 먼저 정하자!',
    emoji: '✖️',
    prerequisiteStepIds: ['NT04'],
  },
  {
    id: 'FRAG-NT05-NM09',
    stepId: 'NT05',
    stepTitle: '정수·유리수의 곱셈·나눗셈',
    misconceptionCode: 'NM09',
    misconceptionLabel: '음수×음수=음수로 계산',
    title: '(-) × (-) = (+)!',
    description: '(-5)×(-4)=-20 처럼 계산하는 실수. 음수×음수는 반드시 양수입니다.',
    fixTip: '음수끼리 곱하면 양수. (-5)×(-4)=+20. "빚을 없애면 이득"!',
    emoji: '🔄',
    prerequisiteStepIds: ['NT04'],
  },
  {
    id: 'FRAG-NT05-NM10',
    stepId: 'NT05',
    stepTitle: '정수·유리수의 곱셈·나눗셈',
    misconceptionCode: 'NM10',
    misconceptionLabel: '나눗셈에서 역수를 취하지 않음',
    title: '분수 나눗셈 = 역수 곱셈!',
    description: '(-3/4)÷(1/2) = -3/8 처럼 분모끼리 나누는 실수. 나누는 수의 역수를 곱해야 합니다.',
    fixTip: '÷(1/2) = ×(2/1). 나눗셈 → 역수 곱셈으로 바꾸기!',
    emoji: '÷',
    prerequisiteStepIds: ['NT04'],
  },
  // ── NT06: 유리수의 개념과 분류 ───────────────────────────
  {
    id: 'FRAG-NT06-NM11',
    stepId: 'NT06',
    stepTitle: '유리수의 개념과 분류',
    misconceptionCode: 'NM11',
    misconceptionLabel: '유리수=분수만이라고 착각',
    title: '정수도 유리수다! (a/1 형태)',
    description: '유리수는 "정수/0이 아닌 정수" 형태. 정수 3은 3/1로 나타낼 수 있으므로 유리수입니다.',
    fixTip: '정수 ⊂ 유리수. 모든 정수는 유리수이지만, 모든 유리수가 정수는 아님!',
    emoji: '🔢',
    prerequisiteStepIds: ['NT01'],
  },
  // ── NT07: 사칙연산 혼합 계산 ─────────────────────────────
  {
    id: 'FRAG-NT07-NM12',
    stepId: 'NT07',
    stepTitle: '사칙연산 혼합 계산',
    misconceptionCode: 'NM12',
    misconceptionLabel: '왼쪽에서 오른쪽으로만 계산',
    title: '혼합 연산 순서: 괄호→곱나눗→덧뺄!',
    description: '3+4×2 = 14가 아닌 11. 곱셈·나눗셈을 덧셈·뺄셈보다 먼저 계산해야 합니다.',
    fixTip: '① 괄호 안 ② × ÷ ③ + - 순서. 같은 우선순위면 왼쪽부터!',
    emoji: '🧮',
    prerequisiteStepIds: ['NT04', 'NT05'],
  },
  {
    id: 'FRAG-NT07-NM13',
    stepId: 'NT07',
    stepTitle: '사칙연산 혼합 계산',
    misconceptionCode: 'NM13',
    misconceptionLabel: '분배법칙 부호 오류',
    title: '분배법칙에서 음수 부호 주의!',
    description: '-2×(3-5) = -2×3 - 2×5 처럼 부호를 빠뜨리는 실수. -2를 분배할 때 부호도 함께!',
    fixTip: '-2×(3-5) = (-2×3)+(-2×(-5)) = -6+10 = 4. 음수 분배 시 부호 확인!',
    emoji: '📐',
    prerequisiteStepIds: ['NT04', 'NT05'],
  },
]

// ─────────────────────────────────────────────────────────────
// N2-2 제곱근과 실수 파편 데이터베이스
// ─────────────────────────────────────────────────────────────
export const N3_FRAGMENT_DB: FragmentDef[] = [
  // ── NQ01: 제곱근의 뜻 ─────────────────────────────────────
  {
    id: 'FRAG-NQ01-NM01',
    stepId: 'NQ01',
    stepTitle: '제곱근의 뜻과 표현',
    misconceptionCode: 'NM01',
    misconceptionLabel: '√9=±3 혼동',
    title: '"9의 제곱근"과 "√9"는 달라요!',
    description: '9의 제곱근은 ±3 (두 개), √9는 양의 제곱근 3 하나만. √ 기호는 양의 값만 가리킵니다.',
    fixTip: '√a = a의 양의 제곱근. "a의 제곱근"은 ±√a (두 개). 기호 차이를 구분하세요!',
    emoji: '√',
    prerequisiteStepIds: [],
  },
  {
    id: 'FRAG-NQ01-NM02',
    stepId: 'NQ01',
    stepTitle: '제곱근의 뜻과 표현',
    misconceptionCode: 'NM02',
    misconceptionLabel: '음수의 제곱근 오해',
    title: '음수의 제곱근은 실수 범위에 없어요!',
    description: 'a < 0이면 실수 범위에서 제곱근이 존재하지 않습니다. √(-4)는 실수가 아닙니다.',
    fixTip: '제곱근이 실수이려면 a ≥ 0이어야 합니다. 0의 제곱근은 0 하나뿐!',
    emoji: '⚠️',
    prerequisiteStepIds: [],
  },
  // ── NQ02: 제곱근의 성질 ──────────────────────────────────
  {
    id: 'FRAG-NQ02-NM03',
    stepId: 'NQ02',
    stepTitle: '제곱근의 성질',
    misconceptionCode: 'NM03',
    misconceptionLabel: '(√a)²=√a 혼동',
    title: '(√a)² = a, √와 ²은 역연산이에요!',
    description: '(√5)² = 5. 루트와 제곱은 서로 역연산이라 사라집니다. √(5²) = |5| = 5.',
    fixTip: '(√a)² = a (a≥0). √(a²) = |a|. 두 공식 모두 결과는 양수!',
    emoji: '📐',
    prerequisiteStepIds: ['NQ01'],
  },
  {
    id: 'FRAG-NQ02-NM04',
    stepId: 'NQ02',
    stepTitle: '제곱근의 성질',
    misconceptionCode: 'NM04',
    misconceptionLabel: '√(a²)=a (음수 무시)',
    title: '√((-3)²) = 3이지 -3이 아니에요!',
    description: '√(a²) = |a|. a가 음수여도 √(a²)은 항상 양수(절댓값)입니다.',
    fixTip: '√((-3)²) = √9 = 3 = |-3|. 루트 안에 제곱이 있으면 절댓값!',
    emoji: '|a|',
    prerequisiteStepIds: ['NQ01'],
  },
  // ── NQ03: 무리수와 실수 ──────────────────────────────────
  {
    id: 'FRAG-NQ03-NM05',
    stepId: 'NQ03',
    stepTitle: '무리수와 실수',
    misconceptionCode: 'NM05',
    misconceptionLabel: '√n은 무조건 무리수',
    title: '√4 = 2는 유리수예요!',
    description: 'n이 완전제곱수이면 √n은 정수(유리수)입니다. √가 있다고 무조건 무리수가 아닙니다.',
    fixTip: '√n 판별: n이 완전제곱수(1,4,9,16...)이면 유리수. 아니면 무리수.',
    emoji: '🌀',
    prerequisiteStepIds: ['NQ01', 'NQ02'],
  },
  {
    id: 'FRAG-NQ03-NM06',
    stepId: 'NQ03',
    stepTitle: '무리수와 실수',
    misconceptionCode: 'NM06',
    misconceptionLabel: '무리수는 실수가 아님',
    title: '무리수도 실수예요! 실수 = 유리수 + 무리수',
    description: '실수는 유리수와 무리수를 모두 포함합니다. 무리수(√2, π)도 수직선 위의 한 점입니다.',
    fixTip: '포함 관계: 자연수 ⊂ 정수 ⊂ 유리수 ⊂ 실수, 무리수 ⊂ 실수',
    emoji: '🌍',
    prerequisiteStepIds: ['NQ03'],
  },
  // ── NQ04: 실수의 대소 비교 ────────────────────────────────
  {
    id: 'FRAG-NQ04-NM07',
    stepId: 'NQ04',
    stepTitle: '실수의 대소 비교',
    misconceptionCode: 'NM07',
    misconceptionLabel: '루트가 있으면 작다는 선입견',
    title: '양수 비교는 제곱해서 판단!',
    description: '√5 > 2. 이유: (√5)²=5 > 2²=4. 양수끼리는 제곱해서 비교하면 부등호가 그대로.',
    fixTip: 'a>0, b>0이면: a>b ⟺ a²>b². 제곱 후 더 큰 쪽이 원래도 더 큽니다.',
    emoji: '📊',
    prerequisiteStepIds: ['NQ01', 'NQ02'],
  },
  // ── NQ05: 제곱근의 곱셈·나눗셈 ───────────────────────────
  {
    id: 'FRAG-NQ05-NM08',
    stepId: 'NQ05',
    stepTitle: '제곱근의 곱셈·나눗셈',
    misconceptionCode: 'NM08',
    misconceptionLabel: '분모 유리화 누락',
    title: '분모에 √가 있으면 유리화해야 해요!',
    description: '1/√2 ≠ 최종 답. 분모·분자에 √2를 곱해 1/√2 = √2/2로 유리화합니다.',
    fixTip: '1/√a = √a/a. 분모의 √를 없애는 과정이 유리화!',
    emoji: '🔢',
    prerequisiteStepIds: ['NQ01', 'NQ02'],
  },
  {
    id: 'FRAG-NQ05-NM09',
    stepId: 'NQ05',
    stepTitle: '제곱근의 곱셈·나눗셈',
    misconceptionCode: 'NM09',
    misconceptionLabel: '제곱인수 미처리',
    title: '√12 = 2√3. 제곱수를 밖으로 꺼내야 해요!',
    description: '√12를 그대로 두면 안 됩니다. 12=4×3이므로 √12=√(4×3)=2√3으로 간단히.',
    fixTip: '제곱인수 찾기: 큰 제곱수(4,9,16,25...)로 나누고 루트 밖으로 꺼내세요.',
    emoji: '✖️',
    prerequisiteStepIds: ['NQ01', 'NQ02'],
  },
  // ── NQ06: 제곱근의 덧셈·뺄셈 ─────────────────────────────
  {
    id: 'FRAG-NQ06-NM10',
    stepId: 'NQ06',
    stepTitle: '제곱근의 덧셈·뺄셈',
    misconceptionCode: 'NM10',
    misconceptionLabel: '√a+√b=√(a+b) 오류',
    title: '√2 + √3 ≠ √5. 루트 안이 같아야만 더할 수 있어요!',
    description: '√2+√3은 더 이상 간단히 할 수 없습니다. 2√3+5√3=7√3처럼 루트 안이 같아야 합산 가능.',
    fixTip: 'a√k + b√k = (a+b)√k. 루트 안의 수가 다르면 더할 수 없습니다.',
    emoji: '➕',
    prerequisiteStepIds: ['NQ05'],
  },
  {
    id: 'FRAG-NQ06-NM11',
    stepId: 'NQ06',
    stepTitle: '제곱근의 덧셈·뺄셈',
    misconceptionCode: 'NM11',
    misconceptionLabel: '간단히 하지 않고 덧셈',
    title: '먼저 근호를 간단히 한 후 같은 루트끼리 더하세요!',
    description: '√8+√18 → 먼저 2√2+3√2 → 5√2. 루트를 간단히 하지 않으면 같은 루트인지 확인이 안 됩니다.',
    fixTip: '① 근호 간단히 (제곱인수 빼기) → ② 같은 루트끼리 계수 합산',
    emoji: '🔄',
    prerequisiteStepIds: ['NQ05'],
  },
  // ── NQ07: 완전제곱수 기초 ────────────────────────────────
  {
    id: 'FRAG-NQ07-NM12',
    stepId: 'NQ07',
    stepTitle: '수포자를 위한 처음부터',
    misconceptionCode: 'NM12',
    misconceptionLabel: '완전제곱수 암기 부족',
    title: '완전제곱수 표를 외우면 절반은 해결!',
    description: '√1=1, √4=2, √9=3, √16=4, √25=5... √100=10. 이 표를 외우면 제곱근 계산이 쉬워집니다.',
    fixTip: '1²~10²의 완전제곱수 암기: 1,4,9,16,25,36,49,64,81,100',
    emoji: '🌱',
    prerequisiteStepIds: [],
  },
]

// ─────────────────────────────────────────────────────────────
// 우선순위 계산 엔진
// ─────────────────────────────────────────────────────────────

const PRIORITY_WEIGHTS = {
  misconceptionHit:     30,  // 오개념 1회 기록 = +30점
  stepNotCompleted:     20,  // 해당 단계 미완료 = +20점
  prerequisiteNotDone:  15,  // 전제 단계 미완료 = +15점 (개당)
  baseScore:            10,  // 기본 점수
} as const

/**
 * 학습 기록을 분석하여 우선순위가 높은 예상 파편 목록을 반환합니다.
 * @param unitProgress  progressStore에서 가져온 단원 진도 데이터
 * @param maxCount      최대 반환 개수 (기본 5)
 */
export function computePredictedFragments(
  unitProgress: UnitProgress | undefined,
  maxCount: number = 5
): PredictedFragment[] {
  if (!unitProgress) return []

  const completedStepIds = new Set(
    Object.entries(unitProgress.learnSteps)
      .filter(([, v]) => v.status === 'completed' || v.status === 'mastered')
      .map(([k]) => k)
  )

  const misconceptionMap = unitProgress.misconceptions ?? {}

  const db = unitProgress.unitId?.startsWith('A1-1')
    ? A1_FRAGMENT_DB
    : unitProgress.unitId?.startsWith('N2-2')
      ? N3_FRAGMENT_DB
      : unitProgress.unitId?.startsWith('N1-2') || unitProgress.unitId?.startsWith('N2-1')
        ? N2_FRAGMENT_DB
        : N1_FRAGMENT_DB

  const results: PredictedFragment[] = db.map(frag => {
    let priority = PRIORITY_WEIGHTS.baseScore
    const reason: string[] = []

    // 1. 오개념 기록 반영
    const mc = misconceptionMap[frag.misconceptionCode]
    if (mc && mc.hitCount > 0) {
      const addPriority = mc.hitCount * PRIORITY_WEIGHTS.misconceptionHit
      priority += addPriority
      reason.push(`❗ ${frag.misconceptionLabel} 오답 ${mc.hitCount}회 기록됨`)
    }

    // 2. 해당 단계 미완료 반영
    if (!completedStepIds.has(frag.stepId)) {
      priority += PRIORITY_WEIGHTS.stepNotCompleted
      reason.push(`📖 ${frag.stepTitle} 단계 미완료`)
    }

    // 3. 전제 단계 미완료 반영
    const undonePre = frag.prerequisiteStepIds.filter(id => !completedStepIds.has(id))
    if (undonePre.length > 0) {
      priority += undonePre.length * PRIORITY_WEIGHTS.prerequisiteNotDone
      reason.push(`🔗 전제 단계 ${undonePre.length}개 미완료`)
    }

    // 태그 결정
    const mc2 = misconceptionMap[frag.misconceptionCode]
    const hitCount = mc2?.hitCount ?? 0
    const tag: PredictedFragment['tag'] =
      hitCount >= 2 ? 'danger' :
      hitCount >= 1 ? 'warning' :
      'review'

    return { fragment: frag, priority, reason, tag }
  })

  // 우선순위 내림차순 정렬, 상위 maxCount개 반환
  return results
    .sort((a, b) => b.priority - a.priority)
    .slice(0, maxCount)
}

/**
 * 테스트 결과의 오답 오개념 코드 목록을 받아
 * 즉시 봐야 할 파편을 반환합니다 (TestPage 오답 분석 연동)
 */
export function computeFragmentsFromWrongAnswers(
  wrongMisconceptionCodes: string[]
): FragmentDef[] {
  const codeSet = new Set(wrongMisconceptionCodes)
  return N1_FRAGMENT_DB.filter(f => codeSet.has(f.misconceptionCode))
}

// ─────────────────────────────────────────────────────────────
// A1-1 문자와 식 파편 데이터베이스
// ─────────────────────────────────────────────────────────────
export const A1_FRAGMENT_DB: FragmentDef[] = [
  // ── AA01: 문자의 사용과 곱셈 기호 생략 ──────────────────────
  {
    id: 'FRAG-AA01-AM01',
    stepId: 'AA01',
    stepTitle: '문자의 사용과 기호 생략',
    misconceptionCode: 'AM01',
    misconceptionLabel: '숫자·문자 순서 혼동',
    title: '숫자는 항상 문자 앞에 써요!',
    description: 'a×3은 3a이지 a3이 아닙니다. 수학에서는 수×문자일 때 수를 앞에 씁니다.',
    fixTip: '수×문자 → 수를 앞에: a×3 = 3a, x×(-2) = -2x',
    emoji: '🔤',
    prerequisiteStepIds: [],
  },
  {
    id: 'FRAG-AA01-AM02',
    stepId: 'AA01',
    stepTitle: '문자의 사용과 기호 생략',
    misconceptionCode: 'AM02',
    misconceptionLabel: '1 생략 미적용',
    title: '계수가 1이면 숫자를 생략해요!',
    description: '1×a = a이지 1a가 아닙니다. -1×a = -a이지 -1a가 아닙니다.',
    fixTip: '1×a = a, (-1)×a = -a. 계수 1은 쓰지 않아요!',
    emoji: '1️⃣',
    prerequisiteStepIds: [],
  },
  // ── AA02: 식의 값 ──────────────────────────────────────────
  {
    id: 'FRAG-AA02-AM03',
    stepId: 'AA02',
    stepTitle: '식의 값',
    misconceptionCode: 'AM03',
    misconceptionLabel: '음수 대입 시 괄호 누락',
    title: '음수를 대입할 땐 반드시 괄호!',
    description: 'x = -2일 때 3x = 3×(-2) = -6. 괄호 없이 3×-2로 쓰면 오류가 발생해요.',
    fixTip: '음수 대입: 반드시 괄호 사용! x=-2 → 3×(-2) = -6',
    emoji: '( )',
    prerequisiteStepIds: ['AA01'],
  },
  {
    id: 'FRAG-AA02-AM04',
    stepId: 'AA02',
    stepTitle: '식의 값',
    misconceptionCode: 'AM04',
    misconceptionLabel: '대입 순서 혼동',
    title: '대입 후에는 일반 계산 순서를 따라요!',
    description: 'x=2일 때 3x+1 → 3(2)+1 = 6+1 = 7. 덧셈 먼저 하면 안 돼요.',
    fixTip: '대입 → 곱셈·나눗셈 → 덧셈·뺄셈 순서!',
    emoji: '🔢',
    prerequisiteStepIds: ['AA01'],
  },
  // ── AA03: 동류항과 식의 계산 ──────────────────────────────
  {
    id: 'FRAG-AA03-AM05',
    stepId: 'AA03',
    stepTitle: '동류항과 일차식의 덧셈·뺄셈',
    misconceptionCode: 'AM05',
    misconceptionLabel: '동류항 아닌 항 합산',
    title: '문자가 달라도 더할 수 없어요!',
    description: '3x + 2y = 5xy가 아닙니다. 동류항끼리만 더할 수 있어요.',
    fixTip: '동류항: 문자와 차수가 같아야! 3x+2y는 더 이상 정리 불가',
    emoji: '📦',
    prerequisiteStepIds: ['AA01', 'AA02'],
  },
  {
    id: 'FRAG-AA03-AM06',
    stepId: 'AA03',
    stepTitle: '동류항과 일차식의 덧셈·뺄셈',
    misconceptionCode: 'AM06',
    misconceptionLabel: 'x와 x² 동류항 혼동',
    title: 'x와 x²는 동류항이 아니에요!',
    description: '3x + 2x² = 5x³이 아닙니다. 차수가 달라서 합칠 수 없어요.',
    fixTip: '동류항 조건: 문자 같고 차수도 같아야! x ≠ x²',
    emoji: '🎯',
    prerequisiteStepIds: ['AA01', 'AA02'],
  },
  // ── AA04: 일차식과 수의 곱셈·나눗셈 ──────────────────────
  {
    id: 'FRAG-AA04-AM07',
    stepId: 'AA04',
    stepTitle: '일차식과 수의 곱셈·나눗셈',
    misconceptionCode: 'AM07',
    misconceptionLabel: '분배법칙 미적용',
    title: '괄호 앞 수는 모든 항에 곱해요!',
    description: '2(x+3) = 2x+3이 아니라 2x+6입니다. 분배법칙을 모든 항에 적용해야 해요.',
    fixTip: '2(x+3) = 2×x + 2×3 = 2x+6. 괄호 안 모든 항에!',
    emoji: '✖️',
    prerequisiteStepIds: ['AA01', 'AA03'],
  },
  {
    id: 'FRAG-AA04-AM08',
    stepId: 'AA04',
    stepTitle: '일차식과 수의 곱셈·나눗셈',
    misconceptionCode: 'AM08',
    misconceptionLabel: '나눗셈을 역수 곱셈으로 변환 미수행',
    title: '나눗셈은 역수의 곱으로 바꿔요!',
    description: '(2x+4)÷2 = (2x+4)×(1/2) = x+2. 나눗셈을 분수 곱셈으로 처리해야 해요.',
    fixTip: 'a÷b = a×(1/b). (2x+6)÷2 = (2x+6)×(1/2) = x+3',
    emoji: '➗',
    prerequisiteStepIds: ['AA01', 'AA04'],
  },
  // ── AA05: 다항식의 계산 ───────────────────────────────────
  {
    id: 'FRAG-AA05-AM09',
    stepId: 'AA05',
    stepTitle: '다항식의 덧셈과 뺄셈',
    misconceptionCode: 'AM09',
    misconceptionLabel: '괄호 앞 빼기 부호 처리 오류',
    title: '-(x+2)는 -x-2예요! 부호 분배 주의!',
    description: '(3x+1)-(x+2) = 3x+1-x-2 = 2x-1. 빼기 기호가 괄호 안 전체에 적용돼요.',
    fixTip: '-(a+b) = -a-b. 빼는 괄호: 부호를 반대로! -(x+2) = -x-2',
    emoji: '➖',
    prerequisiteStepIds: ['AA03', 'AA04'],
  },
  {
    id: 'FRAG-AA05-AM10',
    stepId: 'AA05',
    stepTitle: '다항식의 덧셈과 뺄셈',
    misconceptionCode: 'AM10',
    misconceptionLabel: '상수항 동류항 처리',
    title: '숫자끼리도 동류항이에요!',
    description: '3x+2+x-1에서 2와 -1은 동류항(상수항). 합치면 2x+1.',
    fixTip: '상수항(숫자)끼리도 동류항! 3x+2+x-1 = (3+1)x+(2-1) = 4x+1',
    emoji: '🔢',
    prerequisiteStepIds: ['AA03'],
  },
  // ── AA06: 일차방정식의 기초 ───────────────────────────────
  {
    id: 'FRAG-AA06-AM11',
    stepId: 'AA06',
    stepTitle: '등식과 방정식의 기초',
    misconceptionCode: 'AM11',
    misconceptionLabel: '등식·항등식·방정식 구분 혼동',
    title: '항등식은 모든 x에 성립, 방정식은 특정 x에만!',
    description: '2x = 2x는 항등식(항상 성립), 2x = 4는 방정식(x=2일 때만 성립).',
    fixTip: '항등식: 어떤 x에도 참 | 방정식: 특정 x(해)에서만 참',
    emoji: '⚖️',
    prerequisiteStepIds: ['AA01', 'AA03'],
  },
  {
    id: 'FRAG-AA06-AM12',
    stepId: 'AA06',
    stepTitle: '등식과 방정식의 기초',
    misconceptionCode: 'AM12',
    misconceptionLabel: '이항 시 부호 변환 누락',
    title: '이항할 때 부호를 바꿔야 해요!',
    description: 'x+3=7에서 3을 이항하면 x=7-3=4. 이항 시 부호가 반대로 바뀌어요.',
    fixTip: 'x+a=b → x=b-a. 이항하면 부호 반전!',
    emoji: '↔️',
    prerequisiteStepIds: ['AA03', 'AA05'],
  },
]

// ─────────────────────────────────────────────────────────────
// 태그별 스타일 설정
// ─────────────────────────────────────────────────────────────
export const FRAGMENT_TAG_CONFIG = {
  danger:  { label: '🔴 긴급 복습', color: 'bg-red-50 border-red-300 text-red-700',   badge: 'bg-red-100 text-red-700' },
  warning: { label: '🟡 주의 필요', color: 'bg-amber-50 border-amber-300 text-amber-700', badge: 'bg-amber-100 text-amber-700' },
  review:  { label: '🔵 예습 추천', color: 'bg-blue-50 border-blue-200 text-blue-700',  badge: 'bg-blue-100 text-blue-700' },
} as const
